import { useState, useEffect } from 'react';
import PageHeader from '../components/PageHeader';
import { API_BASE } from '../apiBase';

const colors = {
  bg: '#f0f1f3',
  cardBg: '#ffffff',
  navy: '#071a52',
  navyLight: '#0b2981',
  greenDark: '#5cb83c',
  textPrimary: '#1a1a2e',
  textSecondary: '#6b7280',
  border: '#e2e4e8',
};

const PROXY = API_BASE;

function Duplicates() {
  const [dupes, setDupes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [stocking, setStocking] = useState(false);
  const [stockedCount, setStockedCount] = useState(0);

  // Per-dupe verification state: { [partKey]: { keepIdx, finalQty, status } }
  const [verify, setVerify] = useState({});

  function getDupeKey(dupe) {
    return `${dupe.partNo}__${dupe.colorId}`;
  }

  function scanForDupes() {
    setLoading(true);
    setScanned(false);
    fetch(`${PROXY}/api/inventories`)
      .then(res => res.json())
      .then(json => {
        const items = json.data || [];
        const groups = {};
        items.forEach(lot => {
          const key = `${lot.item?.no}__${lot.color_id}`;
          if (!groups[key]) {
            groups[key] = {
              partNo: lot.item?.no || '',
              name: lot.item?.name || 'Unknown',
              colorName: lot.color_name || '',
              colorId: lot.color_id || 0,
              type: lot.item?.type || 'P',
              locations: [],
            };
          }
          groups[key].locations.push({
            bin: lot.remarks || '(no bin)',
            qty: lot.quantity || 0,
            inventory_id: lot.inventory_id,
            is_stock_room: lot.is_stock_room || false,
          });
        });
        const dupeList = Object.values(groups)
          .filter(g => g.locations.length > 1)
          .sort((a, b) => b.locations.length - a.locations.length);

        setDupes(dupeList);

        // Initialize verification state
        const v = {};
        dupeList.forEach(dupe => {
          const key = getDupeKey(dupe);
          // Pick the best surviving lot: prefer non-INCOMING, non-stockroom bins
          let bestIdx = 0;
          dupe.locations.forEach((loc, idx) => {
            const isIncoming = loc.bin.toUpperCase().includes('INCOMING');
            const bestIsIncoming = dupe.locations[bestIdx].bin.toUpperCase().includes('INCOMING');
            if (bestIsIncoming && !isIncoming) bestIdx = idx;
          });
          const totalQty = dupe.locations.reduce((sum, l) => sum + l.qty, 0);
          v[key] = { keepIdx: bestIdx, finalQty: totalQty, status: 'pending' };
        });
        setVerify(v);
        setScanned(true);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }

  // Move all extra lots to BrickLink stockroom
  async function stockDupes() {
    setStocking(true);
    let count = 0;
    for (const dupe of dupes) {
      const key = getDupeKey(dupe);
      const keepIdx = verify[key]?.keepIdx ?? 0;
      for (let i = 0; i < dupe.locations.length; i++) {
        if (i === keepIdx) continue;
        const loc = dupe.locations[i];
        if (loc.is_stock_room) continue; // already stocked
        try {
          await fetch(`${PROXY}/api/inventories/${loc.inventory_id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ is_stock_room: true }),
          });
          loc.is_stock_room = true;
          count++;
        } catch (e) { /* continue */ }
      }
    }
    setStockedCount(count);
    setStocking(false);
  }

  // Verify a single dupe: merge into keeper, delete extras
  async function verifyDupe(dupe) {
    const key = getDupeKey(dupe);
    const v = verify[key];
    if (!v) return;

    setVerify(prev => ({ ...prev, [key]: { ...prev[key], status: 'processing' } }));

    const keepLot = dupe.locations[v.keepIdx];
    const delta = v.finalQty - keepLot.qty;

    try {
      // Update keeper quantity (delta) and ensure it's out of stockroom
      const updateBody = { is_stock_room: false };
      if (delta !== 0) updateBody.quantity = delta;
      await fetch(`${PROXY}/api/inventories/${keepLot.inventory_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateBody),
      });

      // Delete all other lots
      for (let i = 0; i < dupe.locations.length; i++) {
        if (i === v.keepIdx) continue;
        try {
          await fetch(`${PROXY}/api/inventories/${dupe.locations[i].inventory_id}`, {
            method: 'DELETE',
          });
        } catch (e) { /* continue */ }
      }

      setVerify(prev => ({ ...prev, [key]: { ...prev[key], status: 'done' } }));
    } catch (err) {
      console.error('Verify error:', err);
      setVerify(prev => ({ ...prev, [key]: { ...prev[key], status: 'error' } }));
    }
  }

  // Verify all pending dupes
  async function verifyAll() {
    for (const dupe of dupes) {
      const key = getDupeKey(dupe);
      if (verify[key]?.status === 'pending') {
        await verifyDupe(dupe);
      }
    }
  }

  function setKeepIdx(dupe, idx) {
    const key = getDupeKey(dupe);
    setVerify(prev => ({ ...prev, [key]: { ...prev[key], keepIdx: idx } }));
  }

  function setFinalQty(dupe, qty) {
    const key = getDupeKey(dupe);
    setVerify(prev => ({ ...prev, [key]: { ...prev[key], finalQty: parseInt(qty) || 0 } }));
  }

  function getImageUrl(dupe) {
    if (dupe.type === 'M') return `https://img.bricklink.com/ItemImage/MN/0/${dupe.partNo}.png`;
    return `https://img.bricklink.com/ItemImage/PN/${dupe.colorId}/${dupe.partNo}.png`;
  }

  const totalExtraLots = dupes.reduce((sum, d) => sum + d.locations.length - 1, 0);
  const pendingCount = dupes.filter(d => verify[getDupeKey(d)]?.status === 'pending').length;
  const doneCount = dupes.filter(d => verify[getDupeKey(d)]?.status === 'done').length;

  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bg,
      padding: '20px',
      fontFamily: "'Outfit', sans-serif",
    }}>
      <PageHeader title="Duplicate Lots" icon="⧉" />

      {/* Scan button */}
      <button
        onClick={scanForDupes}
        disabled={loading}
        style={{
          width: '100%', padding: '14px',
          background: loading ? colors.border : colors.navy,
          color: loading ? colors.textSecondary : '#fff',
          border: 'none', borderRadius: '6px',
          fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: '1rem',
          cursor: loading ? 'default' : 'pointer', marginBottom: '16px',
        }}
      >
        {loading ? 'Scanning...' : scanned ? 'Re-scan for Duplicates' : 'Scan for Duplicates'}
      </button>

      {/* Stats */}
      {scanned && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '10px',
          marginBottom: '20px',
        }}>
          {[
            { value: dupes.length, label: 'Duplicate Parts', accent: '#d97706' },
            { value: totalExtraLots, label: 'Extra Lots', accent: '#b45309' },
            { value: doneCount, label: 'Verified', accent: colors.greenDark },
          ].map((s, i) => (
            <div key={i} style={{
              background: colors.cardBg,
              border: `1px solid ${colors.border}`,
              borderBottom: `3px solid ${s.accent}`,
              borderRadius: '6px',
              padding: '14px',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: '1.6rem', fontWeight: 800, color: s.accent }}>{s.value}</div>
              <div style={{ fontSize: '0.62rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '4px' }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Action buttons */}
      {scanned && dupes.length > 0 && (
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          {stockedCount === 0 && (
            <button
              onClick={stockDupes}
              disabled={stocking}
              style={{
                flex: 1, padding: '14px',
                background: stocking ? colors.border : '#d97706',
                color: '#fff', border: 'none', borderRadius: '6px',
                fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: '0.88rem',
                cursor: stocking ? 'default' : 'pointer',
              }}
            >
              {stocking ? 'Stocking duplicates...' : 'Hide Dupes from Store'}
            </button>
          )}
          {stockedCount > 0 && (
            <div style={{
              flex: 1, padding: '12px 14px',
              background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '6px',
              fontSize: '0.82rem', color: '#92400e', fontWeight: 600, textAlign: 'center',
            }}>
              {stockedCount} extra lot{stockedCount !== 1 ? 's' : ''} moved to stockroom
            </div>
          )}
          {pendingCount > 0 && (
            <button
              onClick={verifyAll}
              style={{
                flex: 1, padding: '14px',
                background: colors.greenDark, color: '#fff',
                border: 'none', borderRadius: '6px',
                fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: '0.88rem',
                cursor: 'pointer',
              }}
            >
              Verify All ({pendingCount})
            </button>
          )}
        </div>
      )}

      {/* Empty state */}
      {scanned && dupes.length === 0 && (
        <div style={{
          background: colors.cardBg,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '40px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '12px', color: colors.greenDark }}>✓</div>
          <div style={{ fontSize: '1.1rem', fontWeight: 700, color: colors.textPrimary, marginBottom: '8px' }}>
            No duplicates found!
          </div>
          <div style={{ fontSize: '0.85rem', color: colors.textSecondary }}>
            Every part lives in exactly one bin
          </div>
        </div>
      )}

      {/* All done state */}
      {scanned && dupes.length > 0 && pendingCount === 0 && doneCount === dupes.length && (
        <div style={{
          background: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '6px',
          padding: '20px',
          textAlign: 'center',
          marginBottom: '20px',
        }}>
          <div style={{ fontSize: '1.5rem', marginBottom: '8px', color: colors.greenDark }}>✓</div>
          <div style={{ fontSize: '1.1rem', fontWeight: 800, color: colors.greenDark, marginBottom: '4px' }}>
            All duplicates verified!
          </div>
          <div style={{ fontSize: '0.82rem', color: colors.textSecondary }}>
            {doneCount} part{doneCount !== 1 ? 's' : ''} merged. Your store is clean.
          </div>
        </div>
      )}

      {/* Dupe cards */}
      {scanned && dupes.map((dupe, i) => {
        const key = getDupeKey(dupe);
        const v = verify[key] || { keepIdx: 0, finalQty: 0, status: 'pending' };
        const isDone = v.status === 'done';
        const isProcessing = v.status === 'processing';
        const isError = v.status === 'error';

        return (
          <div key={i} style={{
            background: isDone ? '#f0fdf4' : colors.cardBg,
            border: `1px solid ${isDone ? '#bbf7d0' : colors.border}`,
            borderLeft: `3px solid ${isDone ? colors.greenDark : isError ? '#dc2626' : '#d97706'}`,
            borderRadius: '6px',
            padding: '16px',
            marginBottom: '12px',
            opacity: isDone ? 0.7 : 1,
          }}>
            {/* Part header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '14px',
            }}>
              <img
                src={getImageUrl(dupe)}
                alt={dupe.name}
                style={{
                  width: '52px', height: '52px', objectFit: 'contain',
                  background: '#f9fafb', borderRadius: '6px', padding: '4px',
                  border: `1px solid ${colors.border}`,
                }}
                onError={(e) => { e.target.style.opacity = '0.15'; }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.95rem', fontWeight: 800, color: colors.textPrimary, marginBottom: '3px' }}>
                  {dupe.name}
                </div>
                <div style={{ fontSize: '0.75rem', color: colors.textSecondary }}>
                  {dupe.partNo} · {dupe.colorName}
                </div>
              </div>
              {isDone ? (
                <div style={{
                  background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '4px',
                  padding: '4px 10px', fontSize: '0.7rem', fontWeight: 700, color: colors.greenDark,
                }}>✓ Verified</div>
              ) : (
                <div style={{
                  background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '4px',
                  padding: '4px 10px', fontSize: '0.7rem', fontWeight: 700, color: '#d97706',
                }}>{dupe.locations.length} locations</div>
              )}
            </div>

            {/* Location rows — tap to select keeper */}
            {!isDone && dupe.locations.map((loc, j) => {
              const isKeeper = v.keepIdx === j;
              return (
                <div
                  key={j}
                  onClick={() => setKeepIdx(dupe, j)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: isKeeper ? '#f0fdf4' : '#f9fafb',
                    border: `1px solid ${isKeeper ? '#bbf7d0' : colors.border}`,
                    borderLeft: `3px solid ${isKeeper ? colors.greenDark : 'transparent'}`,
                    borderRadius: '6px',
                    padding: '10px 14px',
                    marginBottom: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {/* Radio indicator */}
                    <div style={{
                      width: '18px', height: '18px', borderRadius: '50%',
                      border: `2px solid ${isKeeper ? colors.greenDark : colors.border}`,
                      background: isKeeper ? colors.greenDark : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      {isKeeper && <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#fff' }} />}
                    </div>
                    <div>
                      <div style={{ fontSize: '0.9rem', fontWeight: 700, color: isKeeper ? colors.greenDark : colors.navy }}>
                        {loc.bin}
                      </div>
                      {isKeeper && (
                        <div style={{ fontSize: '0.62rem', color: colors.greenDark, fontWeight: 600, marginTop: '1px' }}>
                          Keep this lot
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{
                    fontSize: '0.82rem', fontWeight: 600, color: colors.textSecondary,
                  }}>
                    Qty: <span style={{ color: colors.textPrimary, fontWeight: 700 }}>{loc.qty}</span>
                    {loc.is_stock_room && (
                      <span style={{
                        marginLeft: '8px', fontSize: '0.6rem', fontWeight: 700,
                        color: '#d97706', background: '#fff7ed', border: '1px solid #fed7aa',
                        borderRadius: '4px', padding: '1px 5px',
                      }}>stocked</span>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Final quantity + verify button */}
            {!isDone && (
              <div style={{
                display: 'flex', gap: '10px', marginTop: '10px', alignItems: 'flex-end',
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.68rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>
                    Final quantity
                  </div>
                  <input
                    type="number"
                    min="0"
                    value={v.finalQty}
                    onChange={e => setFinalQty(dupe, e.target.value)}
                    style={{
                      width: '100%', padding: '10px 12px', background: '#f9fafb',
                      border: `1px solid ${colors.border}`, borderRadius: '6px',
                      fontFamily: "'Outfit', sans-serif", fontSize: '1.1rem', fontWeight: 700,
                      color: colors.textPrimary, outline: 'none', boxSizing: 'border-box',
                      textAlign: 'center',
                    }}
                  />
                </div>
                <button
                  onClick={() => verifyDupe(dupe)}
                  disabled={isProcessing}
                  style={{
                    padding: '12px 20px',
                    background: isProcessing ? colors.border : colors.greenDark,
                    color: '#fff', border: 'none', borderRadius: '6px',
                    fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: '0.85rem',
                    cursor: isProcessing ? 'default' : 'pointer', whiteSpace: 'nowrap',
                  }}
                >
                  {isProcessing ? 'Merging...' : 'Verify & Merge'}
                </button>
              </div>
            )}

            {/* Error state */}
            {isError && (
              <div style={{
                background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px',
                padding: '10px 14px', marginTop: '10px', fontSize: '0.82rem', color: '#dc2626',
              }}>
                Something went wrong. Try again.
              </div>
            )}

            {/* Done state */}
            {isDone && (
              <div style={{
                fontSize: '0.78rem', color: colors.greenDark, fontWeight: 600, marginTop: '6px',
              }}>
                Merged to {dupe.locations[v.keepIdx]?.bin} with qty {v.finalQty}. Extra lots deleted.
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default Duplicates;